import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Typography, Tabs, Row, Col, Space, Card, Table } from 'antd';
import {
  ClockCircleOutlined,
  SearchOutlined,
  FileSearchOutlined,
  FileExcelOutlined,
  ExceptionOutlined,
  FileDoneOutlined,
  FolderOpenOutlined,
  FileOutlined,
  EditOutlined,
  SendOutlined
} from '@ant-design/icons';
import { STATES } from '../../config/constants/contractStates';
import DownloadListDocuments from '../../components/DownloadList';
import ExportListExcel from '../../components/ExportListExcel';
import FilterTableByDate from './FilterTableByDate';
import DocumentDetailTable from './DocumentDetailTable';
import SearchContract from '../../components/SearchContract';
import { PERSIST_ACTIVE_TAB, PERSIST_PAGESIZE } from '../../state/lease_contract/types';
import PropTypes from 'prop-types';

const { Title } = Typography;
const { TabPane } = Tabs;

/**
 * Component that returns the list of contracts
 * @component
 * @returns {Element} 
 */
const ContractList = ({
  createdDocList,
  awaitingSignDocList,
  validateForAll,
  awaitingNotary,
  awaitingValidation,
  sendedDocList,
  finalizedDocList,
  canceledDocList,
  objectedDocList,
  allDocList,
  activeTab,
  persistActiveTab,
  resetPageSize
}) => {

  const tableStructure = [
    { icon: <SearchOutlined />, title: 'Revisar', statusName: STATES.HALT, documentList: validateForAll ? validateForAll : {} },
    { icon: <FileOutlined />, title: 'Pagados', statusName: STATES.CREATED, documentList: createdDocList ? createdDocList : {} },
    { icon: <ClockCircleOutlined />, title: 'Por validar', statusName: STATES.AWAITING_VALIDATION, documentList: awaitingValidation ? awaitingValidation : {} },
    { icon: <SendOutlined />, title: 'Enviado a Notarisa', statusName: STATES.SENDED, documentList: sendedDocList ? sendedDocList : {} },
    { icon: <EditOutlined />, title: 'Por firmar', statusName: STATES.AWAITING_SIGN, documentList: awaitingSignDocList ? awaitingSignDocList : {} },
    { icon: <FileSearchOutlined />, title: 'En Notarisa', statusName: STATES.AWAITING_NOTARY, documentList: awaitingNotary ? awaitingNotary : {} },
    { icon: <FileExcelOutlined />, title: 'Anulados', statusName: STATES.CANCELED, documentList: canceledDocList ? canceledDocList : {} },
    { icon: <ExceptionOutlined />, title: 'Objetados', statusName: STATES.OBJECTED, documentList: objectedDocList ? objectedDocList : {} },
    { icon: <FileDoneOutlined />, title: 'Finalizados', statusName: STATES.FINALIZED, documentList: finalizedDocList ? finalizedDocList : {} },
    { icon: <FolderOpenOutlined />, title: 'Todos', statusName: '', documentList: allDocList ? allDocList : {} },
  ];

  const [docList, setDocList] = useState(tableStructure);
  const [searchData, setData] = useState();

  useEffect(() => {
    setDocList(tableStructure);
  }, [
    validateForAll,
    createdDocList,
    awaitingValidation,
    awaitingSignDocList,
    sendedDocList,
    awaitingNotary,
    canceledDocList,
    objectedDocList,
    finalizedDocList,
    allDocList
  ]);

  const Tab = (props, key) => (
    <TabPane
      tab={
        <span>
          {props.icon}
          {props.title}
        </span>
      }
      key={key}
    >
      <DocumentDetailTable
        statusName={props.statusName}
        document={props.documentList}
      />
    </TabPane>
  );

  const changeTab = activeKey => {
    resetPageSize({ page: 1, selectedPageSize: 10 });
    persistActiveTab(activeKey);
  };

  return (
    <Card>
      <Row gutter={16}>
        <Col xs={24} sm={24} md={18}>
          <Row gutter={16}>
            <Title level={3}>Contratos de arriendo</Title>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={24} md={20}>
              <Row>
                <Col xs={24} sm={24} md={8}>
                  <FilterTableByDate activeTab={activeTab} />
                </Col>
                <Col xs={24} sm={24} md={16} className="buttons-responsive">
                  <SearchContract searchData={setData} />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={24} md={6} className="d-flex justify-content-end">
          <Space className="buttons-responsive">
            <DownloadListDocuments
              activeTab={activeTab}
              validatedList={validateForAll}
              createdList={createdDocList}
              awaitingValidationList={awaitingValidation}
              awaitingSignList={awaitingSignDocList}
              sendedList={sendedDocList}
              awaitingNotaryList={awaitingNotary}
              canceledList={canceledDocList}
              objectedList={objectedDocList}
              finalizedList={finalizedDocList}
              allList={allDocList}
            />
            <ExportListExcel activeTab={activeTab} />
          </Space>
        </Col>
      </Row>
      {(searchData?.data && searchData?.data[0])
        ?
        <Table
          columns={searchData.columns}
          dataSource={searchData.data}
          scroll={{ x: 1300, y: 400 }}
        />
        :
        <Tabs
          defaultActiveKey={activeTab}
          onChange={activeKey => changeTab(activeKey)}
        >
          {docList.map((list, key) => (Tab(list, key)))}
        </Tabs>
      }
    </Card>
  );
};

ContractList.propTypes = {
  createdDocList: PropTypes.object.isRequired,
  awaitingSignDocList: PropTypes.object.isRequired,
  validateForAll: PropTypes.object.isRequired,
  awaitingNotary: PropTypes.object.isRequired,
  awaitingValidation: PropTypes.object.isRequired,
  sendedDocList: PropTypes.object.isRequired,
  finalizedDocList: PropTypes.object.isRequired,
  canceledDocList: PropTypes.object.isRequired,
  objectedDocList: PropTypes.object.isRequired,
  allDocList: PropTypes.object.isRequired,
  activeTab: PropTypes.string.isRequired,
  persistActiveTab: PropTypes.func.isRequired,
  resetPageSize: PropTypes.func.isRequired,
  title: PropTypes.string,
  icon: PropTypes.object,
  statusName: PropTypes.string,
  documentList: PropTypes.object,
};

const data = (state) => ({
  createdDocList: state.contract.createdDocList,
  awaitingSignDocList: state.contract.awaitingSignDocList,
  validateForAll: state.contract.validateForAll,
  awaitingNotary: state.contract.awaitingNotary,
  awaitingValidation: state.contract.awaitingValidation,
  finalizedDocList: state.contract.finalizedDocList,
  canceledDocList: state.contract.canceledDocList,
  objectedDocList: state.contract.objectedDocList,
  sendedDocList: state.contract.sendedDocList,
  allDocList: state.contract.allDocList,
  moreContractsList: state.contract.moreContractsList,
  activeTab: state.contract.activeTab,
});

const action = (dispatch) => ({
  persistActiveTab: (params) => dispatch({ type: PERSIST_ACTIVE_TAB, payload: params }),
  resetPageSize: (params) => dispatch({ type: PERSIST_PAGESIZE, payload: params }),
});

export default connect(data, action)(ContractList);